import { Icons } from "./Icons";
import { formatTimeFromMs } from "../lib/format";
import { readContextRefs } from "../lib/session";
import { isKitEvent } from "../lib/timeSource";
import type { ConfidenceLevel, EventType, TimelineEvent } from "../api/types";

type TlMeta = {
  cls: string;
  Icon: (typeof Icons)[keyof typeof Icons];
  title: (ev: TimelineEvent) => string;
};

const TL_META: Partial<Record<EventType, TlMeta>> & { default: TlMeta } = {
  attendance_in: { cls: "in", Icon: Icons.arrowIn, title: () => "Entrée" },
  attendance_out: { cls: "out", Icon: Icons.arrowOut, title: () => "Sortie" },
  activity_start: {
    cls: "act",
    Icon: Icons.play,
    title: (e) =>
      `Activité démarrée${e.activity_code ? ` : ${e.activity_code}` : ""}`,
  },
  activity_end: {
    cls: "act-end",
    Icon: Icons.stop,
    title: (e) =>
      `Activité terminée${e.activity_code ? ` : ${e.activity_code}` : ""}`,
  },
  break_start: { cls: "break", Icon: Icons.coffee, title: () => "Pause" },
  break_end: { cls: "break", Icon: Icons.play, title: () => "Reprise" },
  location_ping: { cls: "", Icon: Icons.pin, title: () => "Ping localisation" },
  default: { cls: "", Icon: Icons.list, title: (e) => e.event_type },
};

function getMeta(type: EventType): TlMeta {
  return TL_META[type] ?? TL_META.default;
}

const CONFIDENCE: Record<ConfidenceLevel, { label: string; hint: string }> = {
  declared: { label: "Déclaré", hint: "Saisi par la personne" },
  inferred: {
    label: "Inféré",
    hint: "Déduit automatiquement, sans confirmation",
  },
  verified: { label: "Vérifié", hint: "Confirmé par la source qui l'a capté" },
  imported: { label: "Importé", hint: "Repris d'un autre logiciel" },
};

// source_type backend (record.go:48) → icône + libellé FR pour l'historique.
const SOURCE_META: Record<
  string,
  {
    Icon: (typeof Icons)[keyof typeof Icons];
    label: (ev: TimelineEvent) => string;
  }
> = {
  mobile: { Icon: Icons.mobile, label: () => "Mobile" },
  web: { Icon: Icons.globe, label: () => "Web" },
  device: { Icon: Icons.borne, label: (ev) => `Borne ${ev.source_id}` },
  webhook: {
    Icon: Icons.cloud,
    label: (ev) => (isKitEvent(ev) ? "Kit timesheet" : "Webhook"),
  },
  system: { Icon: Icons.tools, label: () => "Système" },
  offline_replay: { Icon: Icons.sync, label: () => "Hors-ligne" },
  import: { Icon: Icons.cloud, label: () => "Import" },
};

function getSourceMeta(ev: TimelineEvent) {
  return (
    SOURCE_META[ev.source_type] ?? {
      Icon: Icons.borne,
      label: (e: TimelineEvent) => e.source_type || "Inconnu",
    }
  );
}

interface TlRowProps {
  ev: TimelineEvent;
  onEditNote?: () => void;
}

export function TlRow({ ev, onEditNote }: TlRowProps) {
  const meta = getMeta(ev.event_type);
  const IconComp = meta.Icon;
  const source = getSourceMeta(ev);
  const SourceIcon = source.Icon;
  const confidence = CONFIDENCE[ev.confidence_level];
  const {
    note = "",
    projects,
    tasks,
    lteTasks,
  } = readContextRefs(ev.context_refs);
  return (
    <div className={`tl-row ${meta.cls}`}>
      <div className="time">
        {formatTimeFromMs(new Date(ev.occurred_at).getTime())}
      </div>
      <div className="node">
        <IconComp size={14} sw={2.2} />
      </div>
      <div className="body">
        <div className="ttl">{meta.title(ev)}</div>
        <div className="meta-row">
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <SourceIcon size={11} sw={2} />
            {source.label(ev)}
          </span>
          <span className="sep" />
          <span
            className={`confidence ${ev.confidence_level}`}
            title={confidence?.hint}
          >
            <span className="dot" />
            {confidence?.label ?? ev.confidence_level}
          </span>
          {projects.map((p) => (
            <span key={p.id} className="chip chip-pm">
              {p.label}
            </span>
          ))}
          {lteTasks.map((t) => (
            <span key={t.id} className="chip chip-lte">
              {t.label}
            </span>
          ))}
          {tasks.map((t) => (
            <span key={t.id} className="chip chip-tm">
              {t.label}
            </span>
          ))}
        </div>
        {ev.event_type === "activity_start" && onEditNote && (
          <button
            type="button"
            onClick={onEditNote}
            aria-label={note ? "Modifier la note" : "Ajouter une note"}
            className="tl-note"
          >
            <Icons.edit size={13} sw={2} />
            <span className={`text${note ? "" : " placeholder"}`}>
              {note || "Ajouter une note"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

interface TimelineEventListProps {
  events: TimelineEvent[];
  onEditNote?: (ev: TimelineEvent) => void;
  emptyTitle?: string;
  emptySub?: string;
}

export function TimelineEventList({
  events,
  onEditNote,
  emptyTitle = "Aucun événement enregistré ce jour",
  emptySub = "Badgez votre entrée pour commencer.",
}: TimelineEventListProps) {
  const sorted = [...events].sort(
    (a, b) =>
      new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
  );
  return (
    <div className="timeline">
      {sorted.length === 0 && (
        <div className="empty-state">
          <div className="title">{emptyTitle}</div>
          <div className="sub">{emptySub}</div>
        </div>
      )}
      {sorted.map((ev) => (
        <TlRow
          key={ev.id}
          ev={ev}
          onEditNote={
            onEditNote && ev.event_type === "activity_start"
              ? () => onEditNote(ev)
              : undefined
          }
        />
      ))}
    </div>
  );
}
